const API_VERSION = '2024-10'

function getEndpoint(shopId) {
  const base = `https://shopify.com/${shopId}/account/customer/api`
  return `${base}/${API_VERSION}/graphql`
}

export class AuthError extends Error {}

async function customerAccountRequest(
  shopId, accessToken, query, variables = {},
) {
  const resp = await fetch(getEndpoint(shopId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: accessToken,
    },
    body: JSON.stringify({ query, variables }),
  })
  if (resp.status === 401) throw new AuthError('Token expired or revoked')
  if (!resp.ok) throw new Error(`Customer API error: ${resp.status}`)
  return resp.json()
}

async function getCustomerId(shopId, accessToken) {
  const data = await customerAccountRequest(shopId, accessToken, `
    query { customer { id } }
  `)
  return data?.data?.customer?.id
}

// Returns { items: [...], updatedAt: timestamp } or null if never synced
export async function readCartMetafield(shopId, accessToken, key) {
  const data = await customerAccountRequest(
    shopId,
    accessToken,
    `query GetCartMetafield($key: String!) {
      customer {
        metafield(namespace: "custom", key: $key) {
          value
        }
      }
    }`,
    { key },
  )
  const value = data?.data?.customer?.metafield?.value
  if (!value) return null
  try {
    const parsed = JSON.parse(value)
    const raw = Array.isArray(parsed) ? parsed : (parsed.items || [])
    const map = {}
    raw.forEach((i) => {
      if (map[i.variantId]) {
        map[i.variantId].quantity += i.quantity
      } else {
        map[i.variantId] = {
          quantity: i.quantity,
          properties: i.properties || {},
        }
      }
    })
    const items = Object.entries(map).map(([variantId, v]) => ({
      variantId: Number(variantId),
      quantity: v.quantity,
      properties: v.properties,
    }))
    return { items, updatedAt: Array.isArray(parsed) ? null : parsed.updatedAt }
  } catch (e) {
    return null
  }
}

export async function writeCartMetafield(shopId, accessToken, key, items) {
  const customerId = await getCustomerId(shopId, accessToken)
  if (!customerId) return

  const map = {}
  items.forEach((i) => {
    if (map[i.variantId]) {
      map[i.variantId].quantity += i.quantity
    } else {
      map[i.variantId] = {
        quantity: i.quantity,
        properties: i.properties || {},
      }
    }
  })
  const dedupedItems = Object.entries(map).map(([variantId, v]) => ({
    variantId: Number(variantId),
    quantity: v.quantity,
    properties: v.properties,
  }))

  const now = Date.now()
  localStorage.setItem('_tmw_cart_sync_last_write', String(now))
  const payload = { items: dedupedItems, updatedAt: now }

  await customerAccountRequest(
    shopId,
    accessToken,
    `mutation MetafieldsSet($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields { key value }
        userErrors { field message }
      }
    }`,
    {
      metafields: [
        {
          ownerId: customerId,
          namespace: 'custom',
          key,
          value: JSON.stringify(payload),
          type: 'json',
        },
      ],
    },
  )
}

// Returns true if the customer placed an order after the given timestamp
export async function hasPurchasedSince(shopId, accessToken, since) {
  try {
    const data = await customerAccountRequest(
      shopId,
      accessToken,
      `query {
        customer {
          orders(first: 1, sortKey: PROCESSED_AT, reverse: true) {
            nodes { processedAt }
          }
        }
      }`,
    )
    const ts = data?.data?.customer?.orders?.nodes?.[0]?.processedAt
    return ts ? new Date(ts).getTime() > since : false
  } catch (e) {
    return false
  }
}
